import numeral from 'numeral'
import moment from 'moment'

const userLocale =
  navigator.languages && navigator.languages.length
    ? navigator.languages[0]
    : navigator.language

export const formatNumbers = function (value, decimal = 0) {
  const n = numeral()
  n.set(value)
  if (decimal === 0) {
    return new Intl.NumberFormat(userLocale, {
      minimumFractionDigits: 0,
    }).format(value)
  } else if (decimal === 2) {
    return new Intl.NumberFormat(userLocale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value)
  } else if (decimal === 4) {
    return new Intl.NumberFormat(userLocale, {
      minimumFractionDigits: 4,
      maximumFractionDigits: 4,
    }).format(value)
  } else if (decimal === 5) {
    return new Intl.NumberFormat(userLocale, {
      minimumFractionDigits: 5,
      maximumFractionDigits: 5,
    }).format(value)
  } else if (decimal === 7) {
    return new Intl.NumberFormat(userLocale, {
      minimumFractionDigits: 7,
      maximumFractionDigits: 7,
    }).format(value)
  } else {
    return new Intl.NumberFormat(userLocale).format(value)
  }
}

export const normalizeNumberFromLocale = (
  value,
  locale = navigator.language
) => {
  if (typeof value === 'number') {
    // If it's already a number, return it directly
    return value
  }
  if (typeof value !== 'string') {
    throw new TypeError('Invalid input: value must be a string or a number')
  }

  // Use the locale to detect formatting
  const formatter = new Intl.NumberFormat(locale)

  // Extract group and decimal separators for the current locale
  // Use 12345.67 to reliably detect group and decimal separators for the locale.
  // A larger number like 1234567.89 could be used, but it's unnecessary for most cases.
  const parts = formatter.formatToParts(12345.67)
  const groupSeparator =
    parts.find((part) => part.type === 'group')?.value || ''
  const decimalSeparator =
    parts.find((part) => part.type === 'decimal')?.value || '.'
  const sanitizedValue = value
    .split(groupSeparator)
    .join('')
    .split(decimalSeparator)
    .join('.')
  const parsedValue = parseFloat(sanitizedValue)

  if (isNaN(parsedValue)) {
    throw new TypeError('Invalid input: unable to parse the number')
  }

  return parsedValue
}

export default defineNuxtPlugin(() => {
  return {
    provide: {
      normalizeNumberFromLocale,
      formatNumbers,
      formatDate: function (date) {
        if (typeof date == 'object') {
          return `${moment(date).format('YYYY-MM-DD')}`
        } else {
          return date
        }
      },
      parseBlobResponseForDocument: async function (response) {
        const contentType = response.headers['content-type']

        if (contentType.includes('application/json')) {
          const text = await response.data.text()
          const json = JSON.parse(text)

          const binaryData = atob(json.pdf.content)

          const blob = new Blob(
            [
              new Uint8Array(
                Array.from(binaryData).map((c) => c.charCodeAt(0))
              ),
            ],
            { type: 'application/pdf' }
          )
          URL.revokeObjectURL(blob)
          return {
            blob: URL.createObjectURL(blob),
            message: json.key ? json.key : null,
          }
        } else if (contentType.includes('application/pdf')) {
          const blob = new Blob([response.data], { type: contentType })
          return {
            blob: URL.createObjectURL(blob),
            message: 'SUCCESS',
          }
        } else {
          throw new Error('Unexpected response type')
        }
      },
    },
  }
})
